import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { QUERY_KEYS } from "../../utils/constants";
import {
  recordOperatorConsent,
  getOperatorConsentHistory,
  getSmsConsentRevision,
  type SmsConsentStatus,
  type SmsSingleStatusResult,
  type OperatorConsentInput,
} from "../../services/sms.service";
import { smsSingleStatusText } from "./ContactSmsStatus";

// Plain status labels (audit R4-UX4): never show raw enum text such as "unknown" or "number suppressed".
export const SMS_CONSENT_STATUS_LABELS: Record<SmsConsentStatus, string> = {
  opted_in: "Opted in to SMS",
  opted_out: "Opted out of SMS",
  number_suppressed: "Sending blocked: an opt-out is recorded on this number",
  no_usable_phone: "No usable phone number",
  unknown: "No SMS consent recorded",
};

export default function SmsConsentEditor({
  contactId,
  phone,
  status,
  singleStatus,
}: {
  contactId: string;
  phone?: string | null;
  /** The existing CRM consent status: drives the re-subscribe guard and defaults (unchanged). */
  status?: SmsConsentStatus;
  /** The single SMS status (migration 998): only the "Current status" line shows it when available. */
  singleStatus?: SmsSingleStatusResult;
}) {
  const { user } = useAuth();
  const cache = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [optedIn, setOptedIn] = useState(true);
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [resubscribe, setResubscribe] = useState(false);
  const [pending, setPending] = useState<OperatorConsentInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // The number's consent revision as of when this form opened (audit R2-T1). Saving sends it back; if a STOP
  // or any other consent change landed since, the server refuses with SMS_CONSENT_STALE.
  // Audit R3-T1: a fresh save needs a NON-EMPTY revision obtained for THIS contact + number. NULL, an error, a
  // superseded load or a changed contact/number all leave the form unverified: Save stays disabled, the draft is
  // kept, and the agent is asked to retry/refresh. The server also refuses a missing revision on this path.
  type RevisionState =
    | { kind: "loading" }
    | { kind: "ok"; value: string; contactId: string; phone: string }
    | { kind: "unavailable" };
  const [revision, setRevision] = useState<RevisionState>({ kind: "loading" });
  // Audit R4-T7 / R4-UX1: the two confirmations are an attestation about ONE destination (this contact at this
  // number). If the contact or its number changes while the form is open, the note is kept as a draft but both
  // confirmations and any frozen request are cleared, and the agent must confirm the member's request for the NEW
  // destination. A same-destination verification retry keeps the choices.
  const [attestedFor, setAttestedFor] = useState<{
    contactId: string;
    phone: string;
  } | null>(null);
  // What changed since the member's request was attested: the contact, the number, or both (R5-UX3:
  // a contact-only change must not be described as "number changed from N to N").
  const [identityChange, setIdentityChange] = useState<{
    from: string;
    to: string;
    contactChanged: boolean;
    phoneChanged: boolean;
  } | null>(null);
  const loadSeq = useRef(0);
  const [stale, setStale] = useState(false);
  const loadRevision = async () => {
    const mine = ++loadSeq.current;
    const forContact = contactId;
    const forPhone = phone ?? "";
    setRevision({ kind: "loading" });
    try {
      const value = await getSmsConsentRevision(forContact);
      if (mine !== loadSeq.current) {
        return; // a newer load (or a contact/number change) superseded this one
      }
      setRevision(
        typeof value === "string" && value.trim() !== ""
          ? { kind: "ok", value, contactId: forContact, phone: forPhone }
          : { kind: "unavailable" },
      );
    } catch {
      if (mine === loadSeq.current) {
        setRevision({ kind: "unavailable" });
      }
    }
  };
  useEffect(() => {
    if (!editing || !attestedFor) {
      return;
    }
    const now = { contactId, phone: phone ?? "" };
    if (
      attestedFor.contactId !== now.contactId ||
      attestedFor.phone !== now.phone
    ) {
      loadSeq.current += 1; // a verification still in flight belongs to the old destination
      setConfirmed(false);
      setResubscribe(false);
      setPending(null); // a frozen request is bound to the old destination; never replay it against the new one
      setStale(false);
      setError(null);
      setIdentityChange({
        from: attestedFor.phone,
        to: now.phone,
        contactChanged: attestedFor.contactId !== now.contactId,
        phoneChanged: attestedFor.phone !== now.phone,
      });
      setAttestedFor(now);
      setRevision({ kind: "unavailable" });
    }
  }, [contactId, phone, editing, attestedFor]);
  // The contact or number changed under an open form: whatever was verified no longer applies.
  useEffect(() => {
    if (
      revision.kind === "ok" &&
      (revision.contactId !== contactId || revision.phone !== (phone ?? ""))
    ) {
      loadSeq.current += 1;
      setRevision({ kind: "unavailable" });
    }
  }, [contactId, phone, revision]);
  const verified = revision.kind === "ok" ? revision.value : null;
  const canManage =
    !!user?.is_active &&
    [
      "superadmin",
      "admin",
      "manager",
      "support_admin",
      "support_agent",
    ].includes(user.role);
  const suppressed = status === "number_suppressed" || status === "opted_out";
  // Wording only (the guard above is unchanged): when the single status shows no explicit opt-out, the block rests
  // on an opt-out FLAG, which must not be described to staff as the member's own opt-out (owner rule 2026-10-26).
  const flagOnly = !!singleStatus && singleStatus.sms_status !== "opted_out";
  const history = useQuery({
    queryKey: ["sms-consent-history", contactId],
    queryFn: () => getOperatorConsentHistory(contactId),
    retry: false,
  });
  const refresh = () =>
    Promise.all([
      cache.invalidateQueries({ queryKey: ["sms-consent-status", contactId] }),
      cache.invalidateQueries({ queryKey: ["sms-single-status", contactId] }),
      cache.invalidateQueries({ queryKey: ["sms-consent-history", contactId] }),
      cache.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACTS, contactId] }),
    ]);
  function open() {
    setEditing(true);
    setAttestedFor({ contactId, phone: phone ?? "" });
    setIdentityChange(null);
    setOptedIn(status !== "opted_in");
    setNote("");
    setConfirmed(false);
    setResubscribe(false);
    setPending(null);
    setError(null);
    setSaved(false);
    setStale(false);
    void loadRevision();
  }
  // Start a fresh decision against the current state: new revision, new request id, re-confirm.
  // keepDraft (a failed verification, not a stale refusal) keeps the agent's note and confirmations.
  async function refreshAndReview(keepDraft = false) {
    setPending(null);
    setError(null);
    setStale(false);
    if (!keepDraft) {
      setConfirmed(false);
      setResubscribe(false);
    }
    await refresh();
    await loadRevision();
  }
  async function save() {
    const request = pending ?? {
      contactId,
      expectedPhone: phone ?? "",
      optedIn,
      note,
      conversationConfirmed: confirmed,
      resubscribe,
      requestId: crypto.randomUUID(),
      expectedRevision: verified,
      requireRevision: true,
    };
    if (!request.expectedRevision) {
      return; // never an unprotected fresh save (the button is disabled; this is defence in depth)
    }
    setPending(request);
    setBusy(true);
    setError(null);
    try {
      await recordOperatorConsent(request);
      setEditing(false);
      setPending(null);
      setSaved(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("SMS_CONSENT_REVISION_REQUIRED")) {
        setPending(null);
        setRevision({ kind: "unavailable" });
      } else if (msg.includes("SMS_CONSENT_STALE")) {
        setStale(true);
        setError(
          "Consent for this number changed since you opened this form (for example, the member texted STOP). Nothing was saved.",
        );
      } else {
        setError(msg);
      }
    } finally {
      await refresh();
      setBusy(false);
    }
  }
  return (
    <div className="mt-2 space-y-2">
      {canManage && !editing && (
        <button
          type="button"
          disabled={!phone || !status || status === "no_usable_phone"}
          onClick={open}
          className="text-sm font-medium text-blue-700 disabled:text-gray-400"
        >
          Update SMS consent
        </button>
      )}
      {saved && (
        <p role="status" className="text-sm text-green-700">
          Member conversation recorded. Current consent is shown above.
        </p>
      )}
      {editing && (
        <section
          aria-label="Record SMS consent"
          className="space-y-3 rounded border bg-gray-50 p-3"
        >
          <p className="text-sm">
            Record the member’s decision for {phone}. This does not send a text.
          </p>
          {(singleStatus || status) && (
            <p className="text-sm text-gray-700">
              Current status for this number:{" "}
              <strong>
                {singleStatus
                  ? smsSingleStatusText(singleStatus)
                  : status
                    ? (SMS_CONSENT_STATUS_LABELS[status] ?? status)
                    : null}
              </strong>
            </p>
          )}
          {identityChange && (
            <div role="alert" className="text-sm text-amber-800">
              {identityChange.phoneChanged &&
                !identityChange.contactChanged && (
                  <p>
                    This contact’s number changed from{" "}
                    {identityChange.from || "(none)"} to{" "}
                    {identityChange.to || "(none)"}. Your note is kept. Confirm
                    the member’s request for the new number before saving.
                  </p>
                )}
              {identityChange.contactChanged &&
                !identityChange.phoneChanged && (
                  <p>
                    The contact changed. Your note is kept as a draft. Review it
                    and confirm this member’s request for{" "}
                    {identityChange.to || "(none)"} before saving.
                  </p>
                )}
              {identityChange.contactChanged && identityChange.phoneChanged && (
                <p>
                  The contact and number changed (the number is now{" "}
                  {identityChange.to || "(none)"}). Your note is kept as a
                  draft. Review it and confirm this member’s request for{" "}
                  {identityChange.to || "(none)"} before saving.
                </p>
              )}
              {revision.kind !== "ok" && (
                <button
                  type="button"
                  onClick={() => void refreshAndReview(false)}
                  className="mt-1 font-medium text-blue-700 underline"
                >
                  Retry verification
                </button>
              )}
            </div>
          )}
          {revision.kind === "unavailable" && !error && !identityChange && (
            <div role="alert" className="text-sm text-red-700">
              <p>
                We couldn’t verify this number. Refresh the contact before
                saving. Your note and choices are kept.
              </p>
              <button
                type="button"
                onClick={() => void refreshAndReview(true)}
                className="mt-1 font-medium text-blue-700 underline"
              >
                Retry verification
              </button>
            </div>
          )}
          {revision.kind === "loading" && (
            <p className="text-xs text-gray-500">Verifying this number…</p>
          )}
          <fieldset disabled={busy || !!pending} className="space-y-3">
            <label className="block text-sm">
              SMS consent
              <select
                value={optedIn ? "in" : "out"}
                onChange={(e) => {
                  setOptedIn(e.target.value === "in");
                  setConfirmed(false);
                  setResubscribe(false);
                }}
                className="mt-1 block w-full rounded border p-2"
              >
                <option value="in">Opt in — member agreed to SMS</option>
                <option value="out">Opt out — member declined SMS</option>
              </select>
            </label>
            <label className="block text-sm">
              Conversation note
              <textarea
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 block w-full rounded border p-2"
                placeholder="Record when you spoke and what the member agreed to."
              />
            </label>
            <label className="flex gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              I spoke directly with the member and they{" "}
              {optedIn
                ? "agreed to receive SMS at this number"
                : "requested no SMS at this number"}
              .
            </label>
            {optedIn && suppressed && (
              <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm">
                <p>
                  {flagOnly
                    ? "This phone number carries an SMS opt-out flag (it may have been imported rather than requested by the member). "
                    : "This phone number already has an opt-out. "}
                  Re-subscription clears older blocks on this number, including
                  on other contacts that share it. Consent is granted only to
                  this contact.
                </p>
                <label className="flex gap-2">
                  <input
                    type="checkbox"
                    checked={resubscribe}
                    onChange={(e) => setResubscribe(e.target.checked)}
                  />
                  {flagOnly
                    ? "The member explicitly requested re-subscription despite the opt-out flag on this number."
                    : "The member explicitly requested re-subscription despite the previous opt-out."}
                </label>
              </div>
            )}
          </fieldset>
          {error && (
            <div role="alert" className="text-sm text-red-700">
              <p>{error}</p>
              {stale ? (
                <button
                  type="button"
                  onClick={() => void refreshAndReview(false)}
                  className="mt-1 font-medium text-blue-700 underline"
                >
                  Refresh and review
                </button>
              ) : (
                <p>
                  Review the current status before retrying. Retry uses the same
                  record so a lost response cannot apply the decision twice.
                </p>
              )}
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={
                busy ||
                stale ||
                (!pending && !verified) ||
                (!pending &&
                  (!confirmed ||
                    !note.trim() ||
                    (optedIn && suppressed && !resubscribe)))
              }
              className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:bg-gray-300"
            >
              {busy
                ? "Saving…"
                : pending
                  ? "Retry same record"
                  : "Save consent"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                void refresh();
              }}
              className="text-sm"
            >
              Close
            </button>
          </div>
        </section>
      )}
      {(history.data?.length ?? 0) > 0 && (
        <details className="text-xs text-gray-600">
          <summary>Recorded member conversations</summary>
          <ul className="mt-2 space-y-2">
            {history.data?.map((event, index) => (
              <li key={`${event.recorded_at}-${index}`}>
                <strong>{event.opted_in ? "Opted in" : "Opted out"}</strong> ·{" "}
                {event.actor_name} ·{" "}
                {new Date(event.recorded_at).toLocaleString()}
                <p className="whitespace-pre-wrap">{event.note}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
      {history.isError && (
        <p className="text-xs text-gray-500">
          Consent conversation history could not be loaded.
        </p>
      )}
    </div>
  );
}
